import { compact } from 'lodash'
import { initializeObservability, type ObservabilityClientConfig } from '@hello-aria/observability/client'

/**
 * What makalu reports as, when the environment says nothing.
 *
 * `instrumentation.ts` reads these too, so the browser identity and the server
 * identity cannot drift into two different services in Grafana.
 */
export const observabilityFallbacks = {
    environment: 'staging',
    namespace: 'client-portal',
    version: 'unknown'
} as const

/**
 * The cross-origin APIs the browser sends `traceparent` to.
 *
 * Makalu calls its API on its own host (`api.helloaria.eu` and its staging and
 * sandbox siblings), so every request is cross-origin and none of them carries
 * the header by default. That API must allow `traceparent` in its CORS policy,
 * or the browser drops the whole request.
 */
const tracedApiUrls = compact([process.env.NEXT_PUBLIC_BASE_API_URL])

export const observabilityConfig: ObservabilityClientConfig = {
    collectorUrl: process.env.NEXT_PUBLIC_FARO_URL ?? '',
    app: {
        name: 'Makalu',
        version: process.env.NEXT_PUBLIC_APP_VERSION ?? observabilityFallbacks.version,
        environment: process.env.NEXT_PUBLIC_ENVIRONMENT ?? observabilityFallbacks.environment,
        namespace: process.env.NEXT_PUBLIC_FARO_APP_NAMESPACE ?? observabilityFallbacks.namespace
    },
    tracedApiUrls
}

/**
 * Starts Faro, or says why it did not.
 *
 * A missing collector URL is how a local run and a preview without the variable
 * behave, not a failure. The log carries no identifier (ARCH-18).
 */
export const startObservability = () => {
    if (!process.env.NEXT_PUBLIC_FARO_URL) {
        console.info('[Observability] Skipped initialization (no collector URL configured)')
        return
    }

    initializeObservability(observabilityConfig)
}
